import { Cancel, CheckCircle } from '@mui/icons-material';
import React, { useEffect, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';

import CButton from '../../../components/CButton';
import CLocalizedText from '../../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CModal from '../../../components/CModal';
import CPageModule from '../../../components/CPageModule';
import CTextFieldDisplay from '../../../components/CTextFieldDisplay';
import CustomTextField from '../../../components/CustomTextField';
import PageModuleFormGroup from '../../../components/PageModuleFormGroup';
import PageModuleFormRow from '../../../components/PageModuleFormRow';
import XDCheckbox from '../../../components/forms/XDCheckbox';
import Page from '../../../components/layout/Page';
import { ETonalButtonClassNames } from '../../../enums/ETagButtonColors';
import HGeneric from '../../../helpers/HGeneric';
import { updateStateAttribute } from '../../../helpers/helper';
import type { ILocalisationEntry } from '../../../models/ILocalisationEntry';
import type { IRequestState } from '../../../models/IRequestState';
import { SvLocalisationService } from '../../../services/SvLocalisationService';
import { debounce } from '../../../utils/utils';

interface FormModel {
  rootKey?: string;
  textKey: string;
  enDe?: string;
  deDe?: string;
  isFrontendKey: boolean;
  isBackendKey: boolean;
  isHomepage: boolean;
  isActive: boolean;
}

interface State {
  form: FormModel;
  initial?: FormModel;

  initialResponse?: ILocalisationEntry;
  requestStateForGet: IRequestState;
  requestStateForSave: IRequestState;

  showAvailability: boolean;
  isAvailable?: boolean;

  notFound: boolean;
  showRevertModal: boolean;
}

const emptyForm: FormModel = {
  rootKey: undefined,
  textKey: '',
  enDe: '',
  deDe: '',
  isFrontendKey: true,
  isBackendKey: false,
  isHomepage: false,
  isActive: true
};

function toForm(entry: ILocalisationEntry): FormModel {
  return {
    rootKey: entry.rootKey,
    textKey: entry.textKey ?? '',
    enDe: entry.enDe ?? '',
    deDe: entry.deDe ?? '',
    isFrontendKey: entry.isFrontendKey,
    isBackendKey: entry.isBackendKey,
    isHomepage: entry.isHomepage ?? false,
    isActive: entry.isActive
  };
}

export default function PLocalisationEntryPage() {
  const { key } = useParams();
  const navigate = useNavigate();

  const isCreate = key === undefined;
  const headingDictKey = isCreate ? 'i18n.newEntry' : 'i18n.editEntry';

  const [state, setState] = useState<State>({
    form: emptyForm,
    initial: undefined,

    requestStateForGet: { isLoading: false, isError: false },
    requestStateForSave: { isLoading: false, isError: false },

    showAvailability: false,
    isAvailable: undefined,

    notFound: false,
    showRevertModal: false
  });

  const checkAvailability = useMemo(() => {
    return debounce(async (textKeyParam: string) => {
      const trimmed = textKeyParam.trim();
      if (!trimmed) return;

      const resp = await SvLocalisationService.getSingleLocalisationEntry({ textKey: trimmed });
      const ok = await resp.isSuccess();

      updateStateAttribute<State>(
        {
          showAvailability: true,
          isAvailable: !ok
        },
        setState
      );
    }, 1000);
  }, []);
  const showRenameWarning = !isCreate && state.showAvailability && state.isAvailable === true;

  const handleFormChange = (_e: React.ChangeEvent, partialForm: Partial<FormModel>) => {
    updateStateAttribute<State>({ form: { ...state.form, ...partialForm } }, setState);
  };

  const handleTextKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;

    updateStateAttribute<State>({ form: { ...state.form, textKey: next } }, setState);

    const initialKey = (state.initial?.textKey ?? '').trim();
    const nextKey = next.trim();

    const changed = nextKey !== initialKey;

    if (!changed) {
      updateStateAttribute<State>({ showAvailability: false, isAvailable: undefined }, setState);
      return;
    }

    updateStateAttribute<State>({ showAvailability: true, isAvailable: undefined }, setState);
    checkAvailability(nextKey);
  };

  useEffect(() => {
    if (isCreate) {
      updateStateAttribute<State>(
        {
          form: emptyForm,
          initial: undefined,

          requestStateForGet: { isLoading: false, isError: false },
          requestStateForSave: { isLoading: false, isError: false },

          showAvailability: false,
          isAvailable: undefined,

          notFound: false,
          showRevertModal: false
        },
        setState
      );
      return;
    }

    (async () => {
      updateStateAttribute<State>(
        {
          requestStateForGet: { isLoading: true, isError: false },
          notFound: false
        },
        setState
      );

      const resp = await SvLocalisationService.getSingleLocalisationEntry({ textKey: key });
      const ok = await resp.isSuccess();

      if (!ok) {
        updateStateAttribute<State>(
          {
            requestStateForGet: { isLoading: false, isError: true },
            notFound: true,
            form: emptyForm,
            initial: undefined,
            showAvailability: false,
            isAvailable: undefined
          },
          setState
        );
        return;
      }

      const entry = await resp.getData();
      const hydrated = entry ? toForm(entry) : emptyForm;

      updateStateAttribute<State>(
        {
          initialResponse: entry,
          requestStateForGet: { isLoading: false, isError: false },
          notFound: false,
          form: hydrated,
          initial: hydrated,
          showAvailability: false,
          isAvailable: undefined
        },
        setState
      );
    })();
  }, [isCreate, key]);

  const hasChanges = useMemo(() => {
    const base = state.initial ?? emptyForm;
    return (
      state.form.textKey !== base.textKey ||
      (state.form.enDe ?? '') !== (base.enDe ?? '') ||
      (state.form.deDe ?? '') !== (base.deDe ?? '') ||
      state.form.isFrontendKey !== base.isFrontendKey ||
      state.form.isBackendKey !== base.isBackendKey ||
      state.form.isHomepage !== base.isHomepage ||
      state.form.isActive !== base.isActive
    );
  }, [state.form, state.initial]);

  const canSave =
    hasChanges &&
    state.form.textKey.trim().length > 0 &&
    !(state.showAvailability && state.isAvailable === false) &&
    !state.requestStateForSave.isLoading;

  const revert = () => {
    if (isCreate) {
      updateStateAttribute<State>({ form: emptyForm, showAvailability: false, isAvailable: undefined }, setState);
      return;
    }

    if (state.initial) {
      updateStateAttribute<State>({ form: state.initial, showAvailability: false, isAvailable: undefined }, setState);
    }
  };

  const onSave = async () => {
    updateStateAttribute<State>({ requestStateForSave: { isLoading: true, isError: false } }, setState);

    const enDe = state.form.enDe ? state.form.enDe : undefined;
    const deDe = state.form.deDe ? state.form.deDe : undefined;

    const resp = await SvLocalisationService.postSingleLocalisationEntry({
      rootKey: isCreate ? undefined : state.form.rootKey,
      textKey: state.form.textKey.trim(),
      enDe,
      deDe,
      isActive: state.form.isActive,
      isFrontendKey: state.form.isFrontendKey,
      isBackendKey: state.form.isBackendKey,
      isHomepage: state.form.isHomepage
    });

    const ok = await resp.isSuccess();

    updateStateAttribute<State>({ requestStateForSave: { isLoading: false, isError: !ok } }, setState);

    if (ok) {
      navigate('/i18n');
    }
  };

  const keyAvailabilityMsg =
    state.showAvailability && !!state.form.textKey ? (
      state.isAvailable === true ? (
        <div className={'flex flex-col gap-1'}>
          <div className={'flex items-center gap-1 ' + ETonalButtonClassNames.TEAL_GREEN_2_ON_WHITE}>
            <CheckCircle />
            <CLocalizedText dictKey={'i18n.single.textKey.available'} />
          </div>

          {showRenameWarning && (
            <div className={'text-sm text-gray-600'}>
              <CLocalizedText dictKey={'i18n.single.textKey.renameWarning'} />
            </div>
          )}
        </div>
      ) : state.isAvailable === false ? (
        <div className={'flex items-center gap-1 text-red-600'}>
          <Cancel />
          <CLocalizedText dictKey={'i18n.single.textKey.notAvailable'} />
        </div>
      ) : null
    ) : null;

  const dePreview = (
    <div className={'flex flex-col text-base pb-2'}>
      <div className={'text-xs'}>
        <CLocalizedText dictKey={'i18n.single.preview'} />
        {' (de-DE)'}
      </div>
      <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex, rehypeRaw]}>
        {state.form.deDe ?? ''}
      </Markdown>
    </div>
  );

  const enPreview = (
    <div className={'flex flex-col text-base pb-2'}>
      <div className={'text-xs '}>
        <CLocalizedText dictKey={'i18n.single.preview'} />
        {' (en-DE)'}
      </div>
      <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex, rehypeRaw]}>
        {state.form.enDe ?? ''}
      </Markdown>
    </div>
  );

  const pageTitle =
    !state.requestStateForGet.isLoading && !state.requestStateForGet.isError
      ? (state.initial?.textKey ?? CLocalizedTextStringStatic('i18n.newEntry'))
      : undefined;

  const createdAtLabel = CLocalizedTextStringStatic('global.createdAt');
  const updatedAtLabel = CLocalizedTextStringStatic('global.updatedAt');
  const updatedByLabel = CLocalizedTextStringStatic('global.updatedBy');
  const deInputLabel = CLocalizedTextStringStatic('i18n.single.input') + ' (de-DE)';
  const enInputLabel = CLocalizedTextStringStatic('i18n.single.input') + ' (en-DE)';

  return (
    <Page title={pageTitle}>
      <CPageModule>
        <h1>
          <CLocalizedText dictKey={headingDictKey} />
        </h1>
        {state.initialResponse && (
          <>
            <CTextFieldDisplay label={createdAtLabel}>
              {HGeneric.formatString(state.initialResponse.createdAt, {
                in: 'tzTimestamp',
                out: 'localTimestampExtended'
              })}
            </CTextFieldDisplay>
            <CTextFieldDisplay label={updatedAtLabel}>
              {HGeneric.formatString(state.initialResponse.updatedAt, {
                in: 'tzTimestamp',
                out: 'localTimestampExtended'
              })}
            </CTextFieldDisplay>
            <CTextFieldDisplay label={updatedByLabel}>{state.initialResponse.updatedBy?.fullName}</CTextFieldDisplay>
          </>
        )}
      </CPageModule>

      <CPageModule
        style={{ paddingTop: 0, paddingBottom: 0 }}
        apiRequestState={state.requestStateForGet}
        showContent={!state.requestStateForGet.isLoading}
      >
        <PageModuleFormGroup>
          <PageModuleFormRow description={keyAvailabilityMsg ?? ''}>
            <CustomTextField
              label={<CLocalizedText dictKey={'i18n.textKey'} />}
              value={state.form.textKey}
              onChange={handleTextKeyChange}
            />
          </PageModuleFormRow>

          <PageModuleFormRow description={state.form.deDe && dePreview}>
            <CustomTextField
              label={deInputLabel}
              className={'sm:w-full'}
              multiline
              rows={(state.form.deDe?.length ?? 0) >= 350 ? undefined : 5}
              value={state.form.deDe ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleFormChange(e, { deDe: e.target.value });
              }}
              onKeyDown={() => {
                // Support pressing enter to add newline
              }}
            />
          </PageModuleFormRow>

          <PageModuleFormRow description={state.form.enDe && enPreview}>
            <CustomTextField
              label={enInputLabel}
              className={'sm:w-full'}
              multiline
              rows={(state.form.enDe?.length ?? 0) >= 350 ? undefined : 5}
              value={state.form.enDe ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleFormChange(e, { enDe: e.target.value });
              }}
              onKeyDown={() => {
                // Support pressing enter to add newline
              }}
            />
          </PageModuleFormRow>

          <PageModuleFormRow>
            <div className={'flex gap-4'}>
              <XDCheckbox
                checked={state.form.isFrontendKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFormChange(e, { isFrontendKey: e.target.checked });
                }}
              >
                <CLocalizedText dictKey={'i18n.frontend'} />
              </XDCheckbox>
              <XDCheckbox
                checked={state.form.isBackendKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFormChange(e, { isBackendKey: e.target.checked });
                }}
              >
                <CLocalizedText dictKey={'i18n.backend'} />
              </XDCheckbox>
              <XDCheckbox
                checked={state.form.isHomepage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFormChange(e, { isHomepage: e.target.checked });
                }}
              >
                <CLocalizedText dictKey={'i18n.homepage'} />
              </XDCheckbox>
              <XDCheckbox
                checked={!state.form.isActive}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleFormChange(e, { isActive: !e.target.checked });
                }}
              >
                <CLocalizedText dictKey={'i18n.disable'} />
              </XDCheckbox>
            </div>
          </PageModuleFormRow>

          <PageModuleFormRow>
            <div className={'flex gap-2 w-full'}>
              <div className={'w-1/2'}>
                <CButton
                  onClick={() => {
                    updateStateAttribute<State>({ showRevertModal: true }, setState);
                  }}
                  disabled={!hasChanges}
                  isForm
                  style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
                >
                  <CLocalizedText dictKey={'i18n.revertChanges'} />
                </CButton>
              </div>

              <div className={'w-1/2'}>
                <CButton
                  onClick={onSave}
                  disabled={!canSave}
                  style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
                  isForm
                  state={state.requestStateForSave}
                >
                  <CLocalizedText dictKey={'i18n.save'} />
                </CButton>
              </div>
            </div>
          </PageModuleFormRow>
        </PageModuleFormGroup>
      </CPageModule>

      <CModal
        open={state.showRevertModal}
        onClose={() => {
          updateStateAttribute<State>({ showRevertModal: false }, setState);
        }}
      >
        <div className={'flex flex-col gap-4'}>
          <h2 className={'text-base font-semibold text-left'}>
            <CLocalizedText dictKey={'i18n.revertChanges.message'} />
          </h2>
          <div className={'flex justify-end gap-2'}>
            <CButton
              style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
              onClick={() => {
                revert();
                updateStateAttribute<State>({ showRevertModal: false }, setState);
              }}
              isForm
            >
              <CLocalizedText dictKey={'i18n.single.revert.confirm'} />
            </CButton>

            <CButton
              onClick={() => {
                updateStateAttribute<State>({ showRevertModal: false }, setState);
              }}
              isForm
              style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
            >
              <CLocalizedText dictKey={'global.cancel'} />
            </CButton>
          </div>
        </div>
      </CModal>
    </Page>
  );
}
